import { useId, type ReactNode } from 'react';

const GOLD = '#E2B825';
const GOLD_DARK = '#A07C18';
const RED_C = '#CC1F2E';
const NAVY = '#1B2A6B';
const BLACK = '#12121E';
const CREAM = '#F5DCBE';
const HAIR_LIGHT = '#D6CEB6';
const HAIR_DARK = '#3B2216';
const WHITE = '#ffffff';

interface ArtProps {
  suit: string;
  color: string;
}

function Mirrored({ children }: { children: ReactNode }) {
  return (
    <>
      <g>{children}</g>
      <g transform="rotate(180 25 35)">{children}</g>
    </>
  );
}

function FleurDeLis({ id }: { id: string }) {
  return (
    <g id={id}>
      <path
        d="M0 -5.5 C2.2 -5.5 3.3 -3.2 1.8 -1.6
           C3.6 -1.9 4.7 0.3 3.1 1.5
           C4.4 2 4.1 3.6 2.3 3.6 L1.1 3.6 L1.1 5.5
           L-1.1 5.5 L-1.1 3.6 L-2.3 3.6
           C-4.1 3.6 -4.4 2 -3.1 1.5
           C-4.7 0.3 -3.6 -1.9 -1.8 -1.6
           C-3.3 -3.2 -2.2 -5.5 0 -5.5 Z"
        fill={GOLD}
        stroke={GOLD_DARK}
        strokeWidth="0.2"
      />
    </g>
  );
}

const SUIT_PATHS: Record<string, string> = {
  spades:
    'M25 5 C25 5 10 20 10 30 C10 37 16 40 22 37 L20 45 L30 45 L28 37 C34 40 40 37 40 30 C40 20 25 5 25 5Z',
  hearts:
    'M25 42 C15 32 5 25 5 17 C5 10 10 5 17 5 C22 5 25 10 25 14 C25 10 28 5 33 5 C40 5 45 10 45 17 C45 25 35 32 25 42Z',
  diamonds: 'M25 5 L40 25 L25 45 L10 25Z',
  clubs:
    'M25 5 C20 5 16 9 16 14 C16 19 20 22 25 22 C20 22 16 26 16 31 C16 36 20 40 25 40 C30 40 34 36 34 31 C34 26 30 22 25 22 C30 22 34 19 34 14 C34 9 30 5 25 5Z M22 38 L20 45 L30 45 L28 38',
};

// Trapezoid robe/dress body — narrow at collar, wide at waist
const BODY = 'M15 25 Q7 28 6 35 L44 35 Q43 28 35 25 Z';

interface ClipIds {
  lc: string;
  rc: string;
}

function RobeBody({ lc, rc }: ClipIds) {
  return (
    <>
      <path d={BODY} fill={NAVY} clipPath={`url(#${lc})`} />
      <path d={BODY} fill={RED_C} clipPath={`url(#${rc})`} />
      <path d={BODY} fill="none" stroke={GOLD_DARK} strokeWidth="0.5" />
      {/* Center gold divider */}
      <line x1="25" y1="24" x2="25" y2="35" stroke={GOLD} strokeWidth="1.6" />
      {/* Waist band */}
      <rect x="10" y="30.5" width="30" height="2.4" fill={GOLD} />
      <rect
        x="10"
        y="30.5"
        width="30"
        height="2.4"
        fill="none"
        stroke={GOLD_DARK}
        strokeWidth="0.35"
      />
      {/* Scrollwork on each side */}
      <path
        d="M12.5 28.5 Q17 26 21.5 28"
        stroke={GOLD}
        strokeWidth="0.65"
        fill="none"
        opacity="0.9"
      />
      <path
        d="M37.5 28.5 Q33 26 28.5 28"
        stroke={GOLD}
        strokeWidth="0.65"
        fill="none"
        opacity="0.9"
      />
    </>
  );
}

interface FaceProps {
  hairColor: string;
  hasBeard?: boolean;
  redLips?: boolean;
}

function RoyalFace({ hairColor, hasBeard = false, redLips = false }: FaceProps) {
  return (
    <>
      {/* Hair (behind face) */}
      <path d="M13 6 Q7 15 9 29 L16 29 Q12 17 17 8 Z" fill={hairColor} />
      <path d="M37 6 Q43 15 41 29 L34 29 Q38 17 33 8 Z" fill={hairColor} />

      {/* Face oval */}
      <ellipse cx="25" cy="15" rx="7.5" ry="8.5" fill={CREAM} stroke={BLACK} strokeWidth="0.5" />

      {/* Eyebrows */}
      <path
        d="M19 11.4 Q21.5 10.1 23.5 11.2"
        stroke={BLACK}
        strokeWidth="0.6"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M26.5 11.2 Q28.5 10.1 31 11.4"
        stroke={BLACK}
        strokeWidth="0.6"
        fill="none"
        strokeLinecap="round"
      />

      {/* Eyes */}
      <ellipse cx="21.5" cy="13" rx="1.4" ry="1.1" fill={BLACK} />
      <ellipse cx="28.5" cy="13" rx="1.4" ry="1.1" fill={BLACK} />
      <circle cx="22.0" cy="12.5" r="0.45" fill={WHITE} />
      <circle cx="29.0" cy="12.5" r="0.45" fill={WHITE} />

      {/* Nose */}
      <path d="M23.5 16.5 Q25 18.2 26.5 16.5" stroke={BLACK} strokeWidth="0.45" fill="none" />

      {/* Mouth */}
      <path
        d="M21.5 20 Q25 21.5 28.5 20"
        stroke={redLips ? RED_C : BLACK}
        strokeWidth={redLips ? 0.75 : 0.55}
        fill="none"
        strokeLinecap="round"
      />

      {/* Beard (King only) */}
      {hasBeard && (
        <>
          <path d="M18 19 Q25 27.5 32 19 Q30.5 25 25 26 Q19.5 25 18 19 Z" fill={HAIR_LIGHT} />
          <path d="M19.5 19 Q25 23 30.5 19" stroke="#B8B0A0" strokeWidth="0.55" fill="none" />
        </>
      )}
    </>
  );
}

export function KingArt({ suit, color }: ArtProps) {
  const uid = useId().replace(/:/g, '');
  const fl = `kfl-${uid}`;
  const lc = `klc-${uid}`;
  const rc = `krc-${uid}`;
  const sp = SUIT_PATHS[suit] ?? SUIT_PATHS.spades;

  return (
    <svg viewBox="0 0 50 70" className="face-card-art">
      <defs>
        <FleurDeLis id={fl} />
        <clipPath id={lc}>
          <rect x="0" y="0" width="25" height="70" />
        </clipPath>
        <clipPath id={rc}>
          <rect x="25" y="0" width="25" height="70" />
        </clipPath>
      </defs>

      {/* Background suit silhouette */}
      <path
        d={sp}
        fill={color}
        opacity="0.10"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />
      <path
        d={sp}
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.17"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />

      <Mirrored>
        <RoyalFace hairColor={HAIR_LIGHT} hasBeard />

        {/* Crown — 3 fleur-de-lis points, band, gems */}
        <rect
          x="11"
          y="6.5"
          width="28"
          height="3"
          fill={GOLD}
          stroke={GOLD_DARK}
          strokeWidth="0.3"
        />
        <path
          d="M11 6.5 Q14 4.5 17 6.5 Q20.5 3 24 6.5"
          stroke={GOLD_DARK}
          strokeWidth="0.4"
          fill="none"
        />
        <path
          d="M26 6.5 Q29.5 3 33 6.5 Q36 4.5 39 6.5"
          stroke={GOLD_DARK}
          strokeWidth="0.4"
          fill="none"
        />
        <use href={`#${fl}`} transform="translate(17,4.5) scale(0.9)" />
        <use href={`#${fl}`} transform="translate(25,1.8) scale(1.2)" />
        <use href={`#${fl}`} transform="translate(33,4.5) scale(0.9)" />
        <circle cx="25" cy="-1.2" r="1.4" fill={RED_C} />
        <circle cx="16" cy="7.8" r="0.85" fill={RED_C} />
        <circle cx="25" cy="7.8" r="0.85" fill={RED_C} />
        <circle cx="34" cy="7.8" r="0.85" fill={RED_C} />

        {/* Scepter */}
        <line x1="8" y1="10" x2="8" y2="35" stroke={GOLD_DARK} strokeWidth="1.5" />
        <circle cx="8" cy="7" r="2.4" fill="none" stroke={GOLD} strokeWidth="1.1" />
        <line x1="8" y1="4.4" x2="8" y2="9.6" stroke={GOLD} strokeWidth="1.2" />
        <line x1="5.5" y1="7" x2="10.5" y2="7" stroke={GOLD} strokeWidth="1.2" />

        {/* Left sleeve (navy) */}
        <path
          d="M13 25.5 Q6 28 4 33 Q5.5 36 8.5 35 Q11 32.5 14 28 Z"
          fill={NAVY}
          stroke={GOLD_DARK}
          strokeWidth="0.4"
        />

        {/* Ermine collar */}
        <path
          d="M15 23 Q25 28.5 35 23 L35 26.5 Q25 32 15 26.5 Z"
          fill={WHITE}
          stroke="#D0D0D0"
          strokeWidth="0.25"
        />
        <circle cx="20" cy="25.5" r="0.65" fill={BLACK} />
        <circle cx="25" cy="28" r="0.65" fill={BLACK} />
        <circle cx="30" cy="25.5" r="0.65" fill={BLACK} />

        <RobeBody lc={lc} rc={rc} />
      </Mirrored>
    </svg>
  );
}

export function QueenArt({ suit, color }: ArtProps) {
  const uid = useId().replace(/:/g, '');
  const fl = `qfl-${uid}`;
  const lc = `qlc-${uid}`;
  const rc = `qrc-${uid}`;
  const sp = SUIT_PATHS[suit] ?? SUIT_PATHS.spades;

  return (
    <svg viewBox="0 0 50 70" className="face-card-art">
      <defs>
        <FleurDeLis id={fl} />
        <clipPath id={lc}>
          <rect x="0" y="0" width="25" height="70" />
        </clipPath>
        <clipPath id={rc}>
          <rect x="25" y="0" width="25" height="70" />
        </clipPath>
      </defs>

      <path
        d={sp}
        fill={color}
        opacity="0.10"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />
      <path
        d={sp}
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.17"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />

      <Mirrored>
        <RoyalFace hairColor={HAIR_DARK} redLips />

        {/* Tiara */}
        <path
          d="M14 8 Q19 2.5 25 7 Q31 2.5 36 8 Z"
          fill={GOLD}
          stroke={GOLD_DARK}
          strokeWidth="0.25"
        />
        <rect
          x="14"
          y="7.2"
          width="22"
          height="2.4"
          fill={GOLD}
          stroke={GOLD_DARK}
          strokeWidth="0.25"
        />
        <use href={`#${fl}`} transform="translate(25,4.2) scale(1.0)" />
        <circle cx="25" cy="4.2" r="1.2" fill={RED_C} />
        <circle cx="16.5" cy="8.6" r="0.75" fill={RED_C} />
        <circle cx="33.5" cy="8.6" r="0.75" fill={RED_C} />

        {/* Flower (right hand) */}
        <g transform="translate(43,22)">
          <circle r="2.6" fill={RED_C} />
          <circle r="1.2" fill={GOLD} />
          <path d="M0 2.6 L0 8" stroke="#3a6b35" strokeWidth="1" />
          <path d="M-3 0 Q-4.8 -1.2 -4 -3 Q-2 -2.4 -2 0 Z" fill="#3a6b35" />
        </g>

        {/* Right sleeve (red) */}
        <path
          d="M37 25.5 Q44 28 46 33 Q44.5 36 41.5 35 Q39 32.5 36 28 Z"
          fill={RED_C}
          stroke={GOLD_DARK}
          strokeWidth="0.4"
        />

        {/* Necklace */}
        <path d="M20 23 Q25 26.5 30 23" stroke={GOLD} strokeWidth="0.9" fill="none" />
        <circle cx="25" cy="25.8" r="1.2" fill={RED_C} stroke={GOLD} strokeWidth="0.4" />

        {/* Lace collar */}
        <path d="M15 23 Q25 28.5 35 23 L35 26.5 Q25 32 15 26.5 Z" fill={WHITE} opacity="0.92" />

        <RobeBody lc={lc} rc={rc} />
      </Mirrored>
    </svg>
  );
}

export function JackArt({ suit, color }: ArtProps) {
  const uid = useId().replace(/:/g, '');
  const lc = `jlc-${uid}`;
  const rc = `jrc-${uid}`;
  const sp = SUIT_PATHS[suit] ?? SUIT_PATHS.spades;

  return (
    <svg viewBox="0 0 50 70" className="face-card-art">
      <defs>
        <clipPath id={lc}>
          <rect x="0" y="0" width="25" height="70" />
        </clipPath>
        <clipPath id={rc}>
          <rect x="25" y="0" width="25" height="70" />
        </clipPath>
      </defs>

      <path
        d={sp}
        fill={color}
        opacity="0.10"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />
      <path
        d={sp}
        fill="none"
        stroke={color}
        strokeWidth="0.8"
        opacity="0.17"
        transform="translate(25,35) scale(0.92) translate(-25,-25)"
      />

      <Mirrored>
        <RoyalFace hairColor={HAIR_DARK} redLips />

        {/* Feathered cap */}
        <path
          d="M14 9.5 Q14 4 25 4 Q36 4 36 9.5 Z"
          fill={RED_C}
          stroke={GOLD_DARK}
          strokeWidth="0.3"
        />
        <rect
          x="14"
          y="8.8"
          width="22"
          height="2.2"
          fill={GOLD}
          stroke={GOLD_DARK}
          strokeWidth="0.25"
        />
        <circle cx="25" cy="6.5" r="1.1" fill={GOLD} />
        {/* Plume */}
        <path
          d="M31 6 Q37 1.5 41 -2.5 Q40 2 36 4.5 Q39 2.5 42 0.5 Q39 5.5 33.5 7.5 Z"
          fill={WHITE}
          opacity="0.9"
          stroke={GOLD_DARK}
          strokeWidth="0.2"
        />

        {/* Halberd (right side) */}
        <line x1="43" y1="5" x2="43" y2="35" stroke="#8a6d3b" strokeWidth="1.4" />
        <path d="M40 5 L43 -0.5 L46 5 L43 8 Z" fill={GOLD} />
        <path d="M43 1.5 L47.5 0 L45.5 5 Z" fill={GOLD} opacity="0.85" />

        {/* Right sleeve (red) reaching halberd */}
        <path
          d="M37 25.5 Q44 28 46 33 Q44.5 36 41.5 35 Q39 32.5 36 28 Z"
          fill={RED_C}
          stroke={GOLD_DARK}
          strokeWidth="0.4"
        />

        {/* Collar */}
        <path d="M17 23 Q25 27.5 33 23 L33 26.5 Q25 31 17 26.5 Z" fill={WHITE} />

        <RobeBody lc={lc} rc={rc} />

        {/* Diagonal sash */}
        <path d="M13 27 Q25 30.5 37 27" stroke={GOLD} strokeWidth="2" fill="none" />
        <path
          d="M13 27 Q25 30.5 37 27"
          stroke={GOLD_DARK}
          strokeWidth="0.45"
          fill="none"
          opacity="0.7"
        />
      </Mirrored>
    </svg>
  );
}

interface AceSuitProps {
  suit: string;
  color: string;
}

export function AceArt({ suit, color }: AceSuitProps) {
  const uid = useId().replace(/:/g, '');
  const fl = `afl-${uid}`;
  const path = SUIT_PATHS[suit] ?? SUIT_PATHS.spades;

  return (
    <svg viewBox="0 0 50 50" className="face-card-art ace-art">
      <defs>
        <FleurDeLis id={fl} />
      </defs>
      <rect
        x="2"
        y="2"
        width="46"
        height="46"
        rx="3"
        fill="none"
        stroke={color}
        strokeWidth="0.7"
        opacity="0.35"
      />
      <rect
        x="5"
        y="5"
        width="40"
        height="40"
        rx="2"
        fill="none"
        stroke={GOLD}
        strokeWidth="0.45"
        opacity="0.55"
      />
      <use href={`#${fl}`} transform="translate(7,7) scale(0.6)" opacity="0.7" />
      <use href={`#${fl}`} transform="translate(43,7) rotate(90) scale(0.6)" opacity="0.7" />
      <use href={`#${fl}`} transform="translate(43,43) rotate(180) scale(0.6)" opacity="0.7" />
      <use href={`#${fl}`} transform="translate(7,43) rotate(270) scale(0.6)" opacity="0.7" />
      <g transform="translate(5,5) scale(0.8)">
        <path d={path} fill={color} />
        <path d={path} fill="none" stroke={GOLD} strokeWidth="0.6" opacity="0.5" />
      </g>
    </svg>
  );
}

// Standard pip positions for number cards (percentage-based x, y; inverted flag)
type PipPos = [number, number, boolean?];

const PIP_LAYOUTS: Record<number, PipPos[]> = {
  2: [
    [50, 18],
    [50, 82, true],
  ],
  3: [
    [50, 18],
    [50, 50],
    [50, 82, true],
  ],
  4: [
    [30, 18],
    [70, 18],
    [30, 82, true],
    [70, 82, true],
  ],
  5: [
    [30, 18],
    [70, 18],
    [50, 50],
    [30, 82, true],
    [70, 82, true],
  ],
  6: [
    [30, 18],
    [70, 18],
    [30, 50],
    [70, 50],
    [30, 82, true],
    [70, 82, true],
  ],
  7: [
    [30, 18],
    [70, 18],
    [50, 34],
    [30, 50],
    [70, 50],
    [30, 82, true],
    [70, 82, true],
  ],
  8: [
    [30, 18],
    [70, 18],
    [50, 34],
    [30, 50],
    [70, 50],
    [50, 66, true],
    [30, 82, true],
    [70, 82, true],
  ],
  9: [
    [30, 14],
    [70, 14],
    [30, 36],
    [70, 36],
    [50, 50],
    [30, 64, true],
    [70, 64, true],
    [30, 86, true],
    [70, 86, true],
  ],
  10: [
    [30, 14],
    [70, 14],
    [50, 26],
    [30, 38],
    [70, 38],
    [30, 62, true],
    [70, 62, true],
    [50, 74, true],
    [30, 86, true],
    [70, 86, true],
  ],
};

const SUIT_SYMBOL: Record<string, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

interface PipLayoutProps {
  rank: number;
  suit: string;
  color: string;
}

export function PipLayout({ rank, suit, color }: PipLayoutProps) {
  const positions = PIP_LAYOUTS[rank];
  if (!positions) return null;
  const symbol = SUIT_SYMBOL[suit] ?? '♠';

  return (
    <div className="card-pips" style={{ color }}>
      {positions.map(([x, y, inv], i) => (
        <span
          key={i}
          className={`card-pip${inv ? ' card-pip-inv' : ''}`}
          style={{ left: `${x}%`, top: `${y}%` }}
        >
          {symbol}
        </span>
      ))}
    </div>
  );
}
